import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Globe, Send, Loader2, CheckCircle2 } from "lucide-react";
import { CONTACT, SERVICES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];
const empty = { name: "", company: "", email: "", phone: "", service: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[0.7rem] uppercase tracking-[0.2em] font-bold text-forest/60 mb-2">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full rounded-2xl border border-forest/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-african focus:ring-4 focus:ring-african/10 transition-[border-color,box-shadow]";

export const Contact = ({ preselectedService }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (preselectedService) setForm((f) => ({ ...f, service: preselectedService }));
  }, [preselectedService]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, company: form.company || null, phone: form.phone || null, service: form.service || null });
      setSent(true);
      setForm(empty);
      toast.success("Message envoyé. Notre équipe vous recontacte sous 24h.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Impossible d'envoyer le message. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative bg-mist py-24 lg:py-32 overflow-hidden" data-testid="contact-section">
      <div className="absolute -left-40 top-20 w-[600px] h-[600px] rounded-full bg-gold/15 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, ease }} className="lg:col-span-5">
          <p className="eyebrow mb-5">Contact</p>
          <h2 className="font-heading font-extrabold text-forest text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-tight">
            Parlons de la sécurité de <span className="text-african">vos équipes.</span>
          </h2>
          <p className="mt-6 text-ink/65 leading-relaxed">Audit, formation, événement ou équipements : décrivez votre besoin, nous revenons vers vous rapidement.</p>

          <ul className="mt-10 space-y-4">
            {[
              { Icon: Mail, label: "Email", v: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "email" },
              { Icon: Phone, label: "Téléphone / WhatsApp", v: CONTACT.phoneDisplay, href: `tel:${CONTACT.phone}`, id: "phone" },
              { Icon: MapPin, label: "Siège", v: CONTACT.address, href: null, id: "address" },
              { Icon: Globe, label: "Site web", v: CONTACT.website, href: CONTACT.websiteUrl, id: "website" },
            ].map(({ Icon, label, v, href, id }) => (
              <li key={id}>
                <a href={href || undefined} target={id === "website" ? "_blank" : undefined} rel={id === "website" ? "noopener noreferrer" : undefined} data-testid={`contact-info-${id}`} className="group flex items-center gap-4 rounded-2xl bg-white border border-forest/10 px-5 py-4 hover:border-african/50 transition-colors">
                  <span className="w-11 h-11 rounded-xl bg-forest text-gold flex items-center justify-center group-hover:bg-african group-hover:text-white transition-colors"><Icon className="w-5 h-5" /></span>
                  <span>
                    <span className="block text-[0.65rem] uppercase tracking-[0.2em] text-forest/50 font-bold">{label}</span>
                    <span className="block font-heading font-bold text-forest">{v}</span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.1, ease }}
          className="lg:col-span-7 relative rounded-[2.5rem] bg-white border border-forest/10 p-7 sm:p-10 shadow-[0_40px_80px_-50px_rgba(26,79,50,0.4)]"
          data-testid="contact-form"
        >
          {sent ? (
            <div className="flex flex-col items-center justify-center text-center py-16" data-testid="contact-success">
              <CheckCircle2 className="w-16 h-16 text-safety" />
              <h3 className="font-heading font-extrabold text-2xl text-forest mt-6">Message reçu.</h3>
              <p className="text-ink/60 mt-2 max-w-sm">Merci pour votre confiance. Un expert RisQZero vous recontacte sous 24h ouvrées.</p>
              <button type="button" onClick={() => setSent(false)} data-testid="contact-send-another" className="mt-8 text-sm font-bold text-african hover:text-forest transition-colors">Envoyer un autre message</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nom complet *"><input required minLength={2} value={form.name} onChange={set("name")} placeholder="Awa Koné" className={inputCls} data-testid="contact-form-name" /></Field>
              <Field label="Entreprise"><input value={form.company} onChange={set("company")} placeholder="Votre société" className={inputCls} data-testid="contact-form-company" /></Field>
              <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} placeholder="vous@entreprise.com" className={inputCls} data-testid="contact-form-email" /></Field>
              <Field label="Téléphone"><input value={form.phone} onChange={set("phone")} placeholder="+225 ..." className={inputCls} data-testid="contact-form-phone" /></Field>
              <div className="sm:col-span-2">
                <Field label="Service souhaité">
                  <select value={form.service} onChange={set("service")} className={inputCls} data-testid="contact-form-service">
                    <option value="">Choisir un service</option>
                    {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                  </select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Message *"><textarea required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Décrivez votre contexte, vos sites, vos enjeux…" className={`${inputCls} resize-none`} data-testid="contact-form-message" /></Field>
              </div>
              <button type="submit" disabled={loading} data-testid="contact-form-submit" className="sm:col-span-2 group relative overflow-hidden rounded-full bg-forest text-white font-bold py-4 disabled:opacity-60">
                <span className="absolute inset-0 bg-african translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)]" />
                <span className="relative inline-flex items-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {loading ? "Envoi…" : "Envoyer ma demande"}
                </span>
              </button>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
};
